import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu, Popover, Tag, Typography } from 'antd'

import { emptyCharacter } from '../../../../components/EmptyChar'
import { renderDate } from '../../../../components/tables/table-column-renderers'
import { buildSearchFilterProps, sortNumericColumn, sortStringColumn } from '../../../../components/tables/utils'
import { BankingGroup } from '../models'

const renderBankingGroup = (issuersCount: number, bankingGroup: BankingGroup) => {
  if (!issuersCount) {
    return emptyCharacter
  }

  const renderIssuersNames = bankingGroup?.issuers.sort().map(({ id, code, name }) => (
    <p>
      {id} - {code} - {name}
    </p>
  ))

  return (
    <Popover
      content={renderIssuersNames}
      title={
        <Typography.Title level={5}>
          {issuersCount > 1 ? 'Multiple Issuers' : 'Single Issuer'} in this banking group
        </Typography.Title>
      }
    >
      <Tag className="issuers-count">{issuersCount}</Tag>
    </Popover>
  )
}

const renderRating = (rating: string, bankingGroup: BankingGroup) => {
  if (!rating) {
    return emptyCharacter
  }

  const historicalRatings = bankingGroup?.ratings.map(({ id, startDate, shortRating, longRating }) => (
    <p>
      {renderDate(startDate)} - <Tag color="blue">{shortRating}</Tag> <Tag color="cyan">{longRating}</Tag>
    </p>
  ))

  return (
    <Popover content={historicalRatings} title={<Typography.Title level={5}>Historical Rating</Typography.Title>}>
      <Tag className="issuers-count">{rating}</Tag>
    </Popover>
  )
}

const renderActionsMenu =
  ({ onUpdate }: { onUpdate: (group: BankingGroup) => void }) =>
  (_: any, group: BankingGroup) => {
    const menu = (
      <Menu>
        <Menu.Item key="updateBankingGroupRating" onClick={() => onUpdate(group)}>
          Update Banking Group Rating
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

export const buildBankingGroupColumns = ({ onUpdate }: { onUpdate: (group: BankingGroup) => void }) => [
  {
    title: 'ID',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Code',
    dataIndex: 'code',
    sorter: sortStringColumn({ dataIndex: 'code' }),
    ...buildSearchFilterProps({ dataIndex: 'code' }),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: sortStringColumn({ dataIndex: 'name' }),
    ...buildSearchFilterProps({ dataIndex: 'name' }),
  },
  {
    title: 'Issuers',
    dataIndex: 'issuersCount',
    sorter: sortNumericColumn({ dataIndex: 'issuersCount' }),
    render: renderBankingGroup,
  },
  {
    title: 'Short Rating',
    dataIndex: 'currentShortRating',
    sorter: sortStringColumn({ dataIndex: 'shortRating' }),
    ...buildSearchFilterProps({ dataIndex: 'shortRating' }),
    render: renderRating,
  },
  {
    title: 'Long Rating',
    dataIndex: 'currentLongRating',
    sorter: sortStringColumn({ dataIndex: 'longRating' }),
    ...buildSearchFilterProps({ dataIndex: 'longRating' }),
    render: renderRating,
  },
  { title: '', render: renderActionsMenu({ onUpdate }) },
]
